import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, downloadFile } from '../../api';
import BidReview from './BidReview';
import { parseDate, fmtDate as fmtCalendarDate } from '../../dateUtil';
import { STEP_LABELS, nextStep, actorFor, cancellable } from '../../stepFlows';
import { displayName, isDeactivated } from '../../userDisplay';
import { formatCurrency } from '../../currencyUtil';

// Label for the seller's advance action from each step; shown only when the
// transaction's own flow says the seller is the actor for its current step.
const SELLER_ACTION_LABEL = {
  awaiting_payment:     'Confirm Payment Received',
  payment_confirmed:    'Mark Ready for Pickup',   // pay_first_no_inspection only
  inspection_scheduled: 'Mark Awaiting Pickup',
};
const sellerActionLabel = (txn) =>
  actorFor(txn.step_flow, txn.step) === 'seller' ? SELLER_ACTION_LABEL[txn.step] : null;

const DEAL_STATUS = {
  draft:    { color: 'text-amber-600 dark:text-amber-400',     dot: 'bg-amber-400',   label: 'Draft' },
  sent:     { color: 'text-blue-600 dark:text-blue-400',       dot: 'bg-blue-500',    label: 'Sent' },
  complete: { color: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', label: 'Complete' },
  archived: { color: 'text-slate-500 dark:text-slate-400',     dot: 'bg-slate-400',   label: 'Archived' },
};

function carLabel(t) {
  return [t.year, t.make, t.model, t.trim].filter(Boolean).join(' ') || t.vin || `Car #${t.car_id}`;
}

function money(n) {
  if (n == null) return '—';
  return `$${formatCurrency(n)}`;
}

function fmtDate(date) {
  if (!date) return '—';
  return fmtCalendarDate(date);
}

function Countdown({ date }) {
  if (!date) return <span className="text-slate-400">—</span>;
  // Due dates are calendar days — payment isn't overdue until that day has ended locally.
  const due = parseDate(date);
  const deadline = new Date(due.getFullYear(), due.getMonth(), due.getDate() + 1);
  const diff = deadline - new Date();
  if (diff < 0) return <span className="text-red-600 font-semibold">Overdue</span>;
  const days = Math.floor(diff / 86400000);
  if (days > 1) return <span>{days} days</span>;
  if (days === 1) return <span className="text-amber-600 font-semibold">1 day</span>;
  return <span className="text-red-600 font-semibold">Today</span>;
}

function StepBadge({ step }) {
  const colors = {
    awaiting_payment:     'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:ring-amber-800',
    payment_confirmed:    'bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:ring-teal-800',
    awaiting_inspection:  'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-900/30 dark:text-sky-400 dark:ring-sky-800',
    inspection_scheduled: 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-800',
    awaiting_pickup:      'bg-purple-50 text-purple-700 ring-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:ring-purple-800',
    closed:               'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:ring-emerald-800',
    cancelled:            'bg-red-50 text-red-600 ring-red-200 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-800',
  };
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ring-1 ${colors[step] || ''}`}>
      {STEP_LABELS[step]}
    </span>
  );
}

function WaitingMsg({ children }) {
  return (
    <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
      <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {children}
    </div>
  );
}

function CancelDialog({ dialog, onClose, onConfirm, loading, triggerRef }) {
  // Escape-to-close and focus restore, matching the Buy Now confirmation modal in DealView.jsx.
  useEffect(() => {
    if (!dialog) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, onClose]);

  const handleClose = () => {
    onClose();
    triggerRef?.current?.focus();
  };

  if (!dialog) return null;
  const { mode, packageCount } = dialog;
  const isRequest = mode === 'request';
  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div role="dialog" aria-modal="true" className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 max-h-[85dvh] overflow-y-auto">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isRequest ? 'bg-amber-50 dark:bg-amber-900/30' : 'bg-red-50 dark:bg-red-900/30'}`}>
          <svg className={`w-6 h-6 ${isRequest ? 'text-amber-500' : 'text-red-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
          {isRequest ? 'Request Cancellation' : 'Confirm Cancellation'}
        </h2>
        {packageCount > 1 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            <span className="font-semibold text-red-600 dark:text-red-400">Package deal:</span> This car is part of a {packageCount}-car deal.{' '}
            {isRequest
              ? 'The buyer will need to confirm cancellation of the entire deal.'
              : `Confirming will cancel all ${packageCount} cars in this deal.`}
          </p>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            {isRequest
              ? 'A cancellation request will be sent to the buyer. The transaction continues until they confirm.'
              : 'This will permanently cancel the transaction. This cannot be undone.'}
          </p>
        )}
        <div className="flex gap-2">
          <button onClick={handleClose} className="btn-secondary flex-1" disabled={loading}>Back</button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {loading ? 'Processing…' : isRequest ? 'Send Request' : 'Confirm Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Groups a flat list of transactions (each already carrying deal_id/deal_name/deal_uuid/deal_status)
// into per-deal buckets, preserving each transaction's original relative order within its group.
function groupByDeal(rows) {
  const map = new Map();
  for (const row of rows) {
    const key = row.deal_id ?? `_${row.id}`;
    if (!map.has(key)) {
      map.set(key, {
        deal_id: row.deal_id, deal_name: row.deal_name,
        deal_uuid: row.deal_uuid, deal_status: row.deal_status,
        rows: [],
      });
    }
    map.get(key).rows.push(row);
  }
  return [...map.values()];
}

function DealHeader({ group, navigate }) {
  const ds = DEAL_STATUS[group.deal_status] || DEAL_STATUS.sent;
  return (
    <div className="flex items-center gap-3 px-5 py-3 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
      {group.deal_uuid ? (
        <button
          onClick={() => navigate(`/seller/deals/${group.deal_uuid}`)}
          className="font-semibold text-slate-800 dark:text-slate-100 hover:underline truncate min-w-0"
        >
          {group.deal_name || `Deal #${group.deal_id}`}
        </button>
      ) : (
        <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">{group.deal_name || 'Deal'}</span>
      )}
      <span className={`inline-flex items-center gap-1.5 text-xs font-medium shrink-0 ${ds.color}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${ds.dot} shrink-0`} />
        {ds.label}
      </span>
      <div className="flex-1" />
      <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">
        {group.rows.length} car{group.rows.length !== 1 ? 's' : ''}
      </span>
    </div>
  );
}

export default function SellerTransactions() {
  const [tab, setTab] = useState('bids');
  const [bidPendingCount, setBidPendingCount] = useState(0);
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingActive, setLoadingActive] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [advancing, setAdvancing] = useState({});
  const [error, setError] = useState(null);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [withdrawing, setWithdrawing] = useState(null);
  const cancelTriggerRef = React.useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api.get('/transactions')
      .then(d  => { if (mounted) setActive(d); })
      .catch(e => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoadingActive(false); });
    return () => { mounted = false; };
  }, []);

  const switchToHistory = () => {
    setTab('history');
    if (!historyLoaded) {
      setLoadingHistory(true);
      api.get('/transactions?history=true')
        .then(rows => { setHistory(rows); setHistoryLoaded(true); })
        .catch(e => setError(`Failed to load history: ${e.message}`))
        .finally(() => setLoadingHistory(false));
    }
  };

  const advanceStep = async (txn) => {
    const target = nextStep(txn.step_flow, txn.step);
    if (!target) return;
    setAdvancing(a => ({ ...a, [txn.id]: true }));
    try {
      const updated = await api.put(`/transactions/${txn.id}`, { step: target });
      // In the pay-last flow the seller's payment confirmation closes the
      // transaction — drop it from the active list instead of showing 'closed'.
      setActive(t => updated.step === 'closed'
        ? t.filter(x => x.id !== txn.id)
        : t.map(x => x.id === txn.id ? { ...x, step: updated.step } : x));
    } finally {
      setAdvancing(a => ({ ...a, [txn.id]: false }));
    }
  };

  const markTitleDelivered = async (txn) => {
    try {
      const updated = await api.put(`/transactions/${txn.id}/title-delivered`);
      setActive(t => t.map(x => x.id === txn.id ? { ...x, title_delivered_at: updated.title_delivered_at } : x));
    } catch (err) {
      setError(err.message);
    }
  };

  // deal_id → count of active transactions for that deal
  const dealCounts = active.reduce((acc, t) => {
    if (t.deal_id) acc[t.deal_id] = (acc[t.deal_id] || 0) + 1;
    return acc;
  }, {});

  const openCancel = (txn, mode, e) => {
    cancelTriggerRef.current = e?.currentTarget || null;
    const packageCount = txn.deal_id ? (dealCounts[txn.deal_id] || 1) : 1;
    setCancelDialog({ txn, mode, packageCount });
  };

  const withdrawCancel = async (txnId) => {
    setWithdrawing(txnId);
    try {
      const updated = await api.put(`/transactions/${txnId}/withdraw-cancel`);
      setActive(t => t.map(x => x.id === txnId ? { ...x, ...updated } : x));
    } catch (err) {
      setError(err.message);
    } finally {
      setWithdrawing(null);
    }
  };

  const handleCancelConfirm = async () => {
    setCancelLoading(true);
    try {
      if (cancelDialog.mode === 'request') {
        const { transactions: updated } = await api.put(`/transactions/${cancelDialog.txn.id}/request-cancel`);
        const map = Object.fromEntries(updated.map(t => [t.id, t]));
        setActive(ts => ts.map(t => map[t.id] ? { ...t, ...map[t.id] } : t));
      } else {
        const { ids } = await api.put(`/transactions/${cancelDialog.txn.id}/confirm-cancel`);
        setActive(ts => ts.filter(t => !ids.includes(t.id)));
      }
      setCancelDialog(null);
    } catch (err) {
      setError(err.message);
      setCancelDialog(null);
    } finally {
      setCancelLoading(false);
    }
  };

  const loading = tab === 'active' ? loadingActive : loadingHistory;

  const sellerHasAction = (txn) =>
    !!sellerActionLabel(txn) || txn.cancel_requested_by === 'buyer';

  // Group Active by deal; deals with an actionable transaction sort first,
  // and within each deal, actionable transactions sort first too.
  const activeDealGroups = groupByDeal(active)
    .map(g => ({ ...g, rows: [...g.rows].sort((a, b) => (sellerHasAction(b) ? 1 : 0) - (sellerHasAction(a) ? 1 : 0)) }))
    .map(g => ({ ...g, actionCount: g.rows.filter(sellerHasAction).length }))
    .sort((a, b) => b.actionCount - a.actionCount || (a.deal_id ?? 0) - (b.deal_id ?? 0));

  // Group History by deal; most recently closed deal first.
  const historyDealGroups = groupByDeal(history)
    .map(g => {
      const latestClosedAt = g.rows.reduce((max, t) =>
        t.closed_at && (!max || new Date(t.closed_at) > new Date(max)) ? t.closed_at : max, null);
      return { ...g, latestClosedAt };
    })
    .sort((a, b) => new Date(b.latestClosedAt || 0) - new Date(a.latestClosedAt || 0));

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Sales</h1>

      <div className="flex gap-1 mb-6 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
        <button
          onClick={() => setTab('bids')}
          className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'bids'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
        >
          Bid Review
          {bidPendingCount > 0 && (
            <span className="ml-1.5 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded-full">
              {bidPendingCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('active')}
          className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'active'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
        >
          Active
          {active.length > 0 && (
            <span className="ml-1.5 text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-full">
              {active.length}
            </span>
          )}
        </button>
        <button
          onClick={switchToHistory}
          className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'history'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
        >
          History
        </button>
      </div>

      {tab === 'bids' ? (
        <BidReview onPendingChange={setBidPendingCount} />
      ) : loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : tab === 'active' ? (
        !active.length ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">No active transactions</h3>
            <p className="text-sm text-slate-400 dark:text-slate-500">Accepted bids will appear here once buyers are under contract.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {activeDealGroups.map((group) => (
              <div key={group.deal_id ?? group.rows[0].id} className="card overflow-hidden">
                <DealHeader group={group} navigate={navigate} />
                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                  {group.rows.map((txn) => (
                    <div key={txn.id} className={`p-5 ${txn.cancel_requested_by === 'buyer' ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
                      <div className="flex flex-wrap sm:flex-nowrap items-start gap-5">
                        <div className="w-24 h-16 bg-slate-100 dark:bg-slate-700 rounded-xl shrink-0 overflow-hidden flex items-center justify-center">
                          {txn.photos_videos?.[0]
                            ? <img src={txn.photos_videos[0]} alt="" className="w-full h-full object-cover" />
                            : <svg className="w-8 h-6 text-slate-300 dark:text-slate-600" viewBox="0 0 60 40" fill="currentColor"><path d="M8 28 Q12 14 20 12 L40 12 Q48 14 52 28 L54 34 Q54 37 51 37 L9 37 Q6 37 6 34 Z" opacity="0.5"/><circle cx="17" cy="37" r="5"/><circle cx="43" cy="37" r="5"/></svg>
                          }
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-semibold text-slate-800 dark:text-slate-100">{carLabel(txn)}</span>
                            <StepBadge step={txn.step} />
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {txn.vin ? `VIN: ${txn.vin}` : 'No VIN'}
                            {txn.mileage ? ` · ${Number(txn.mileage).toLocaleString()} mi` : ''}
                          </p>
                          <div className="flex items-center gap-5 mt-2 flex-wrap">
                            <div>
                              <p className="text-xs text-slate-400 dark:text-slate-500">Buyer</p>
                              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                {txn.buyer_account_name || '—'}
                                {(txn.buyer_fname || txn.buyer_lname) && (
                                  <span className="font-normal text-slate-500 dark:text-slate-400"> · {displayName({ fname: txn.buyer_fname, lname: txn.buyer_lname, id: txn.buyer_id })}</span>
                                )}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-400 dark:text-slate-500">Accepted Bid</p>
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{money(txn.bid_price)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-400 dark:text-slate-500">Payment Due</p>
                              <p className="text-sm text-slate-700 dark:text-slate-300">
                                {fmtDate(txn.payment_due_date)}
                                {txn.payment_due_date && (
                                  <span className="ml-1.5 text-xs text-slate-400">(<Countdown date={txn.payment_due_date} />)</span>
                                )}
                              </p>
                            </div>
                          </div>

                          {/* Step-specific messages */}
                          {txn.step === 'awaiting_payment' && (
                            txn.payment_sent_at ? (
                              <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 ring-1 ring-emerald-200 dark:ring-emerald-800 px-2.5 py-1 rounded-full">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                                Buyer marked payment sent · {new Date(txn.payment_sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>
                            ) : (
                              <WaitingMsg>Awaiting buyer payment confirmation</WaitingMsg>
                            )
                          )}
                          {txn.step === 'payment_confirmed' && actorFor(txn.step_flow, txn.step) === 'buyer' && (
                            <WaitingMsg>Awaiting buyer to schedule inspection</WaitingMsg>
                          )}
                          {txn.step === 'awaiting_inspection' && (
                            <WaitingMsg>Awaiting buyer to schedule inspection</WaitingMsg>
                          )}
                          {txn.step === 'awaiting_pickup' && (
                            <WaitingMsg>Awaiting buyer to confirm pickup</WaitingMsg>
                          )}
                        </div>

                        <div className="w-full sm:w-auto shrink-0 flex sm:block justify-end">
                          {sellerActionLabel(txn) && !txn.cancel_requested_by && (
                            <button
                              onClick={() => advanceStep(txn)}
                              disabled={advancing[txn.id]}
                              className="btn-primary text-xs px-3 py-1.5 whitespace-nowrap"
                            >
                              {advancing[txn.id]
                                ? <span className="flex items-center gap-1.5"><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Updating…</span>
                                : sellerActionLabel(txn)
                              }
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Cancel row */}
                      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/50 flex items-center">
                        {txn.cancel_requested_by === 'seller' ? (
                          <div className="flex items-center gap-3">
                            <WaitingMsg>Cancellation requested — awaiting buyer confirmation</WaitingMsg>
                            <button
                              onClick={() => withdrawCancel(txn.id)}
                              disabled={withdrawing === txn.id}
                              className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 border border-slate-200 dark:border-slate-600 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {withdrawing === txn.id ? 'Withdrawing…' : 'Withdraw Request'}
                            </button>
                          </div>
                        ) : txn.cancel_requested_by === 'buyer' ? (
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Buyer requested cancellation</span>
                            <button
                              onClick={(e) => openCancel(txn, 'confirm', e)}
                              className="text-xs font-semibold text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 px-2.5 py-1 rounded-lg transition-colors"
                            >
                              Confirm Cancel
                            </button>
                          </div>
                        ) : cancellable(txn.step_flow, txn.step) ? (
                          <button
                            onClick={(e) => openCancel(txn, 'request', e)}
                            className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-700 border border-slate-200 dark:border-slate-600 px-2.5 py-1 rounded-lg transition-colors"
                          >
                            Request Cancellation
                          </button>
                        ) : null}
                        <div className="ml-auto">
                          {txn.title_delivered_at ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                              </svg>
                              Title delivered · {new Date(txn.title_delivered_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          ) : (
                            <button
                              onClick={() => markTitleDelivered(txn)}
                              className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:border-emerald-300 dark:hover:border-emerald-700 border border-slate-200 dark:border-slate-600 px-2.5 py-1 rounded-lg transition-colors"
                            >
                              Mark Title Delivered
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        !history.length ? (
          <div className="card py-16 text-center">
            <p className="text-slate-400 text-sm">No completed sales yet.</p>
            <p className="text-slate-400 text-xs mt-1">Closed transactions will appear here.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {historyDealGroups.map((group) => (
              <div key={group.deal_id ?? group.rows[0].id} className="card overflow-hidden">
                <DealHeader group={group} navigate={navigate} />
                <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                      <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Car</th>
                      <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Buyer</th>
                      <th className="py-2.5 px-4 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Sale Price</th>
                      <th className="hidden sm:table-cell py-2.5 px-4 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Closed</th>
                      <th className="py-2.5 px-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map(t => (
                      <tr key={t.id} className={`border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors ${t.step === 'cancelled' ? 'opacity-60' : ''}`}>
                        <td className="py-3 px-4">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{carLabel(t)}</p>
                          {t.vin && [t.year, t.make].some(Boolean) && (
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-mono">{t.vin}</p>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-sm text-slate-700 dark:text-slate-200">
                            {isDeactivated({ fname: t.buyer_fname })
                              ? 'Former user'
                              : ([t.buyer_fname, t.buyer_lname].filter(Boolean).join(' ') || t.buyer_email || '—')}
                          </p>
                          {t.buyer_account_name && (
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{t.buyer_account_name}</p>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {t.step === 'cancelled'
                            ? <span className="text-xs font-semibold text-red-500 dark:text-red-400">Cancelled</span>
                            : <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{money(t.bid_price)}</span>
                          }
                        </td>
                        <td className="hidden sm:table-cell py-3 px-4 text-right text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                          {fmtDate(t.closed_at)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {t.step === 'closed' && (
                            <button
                              onClick={() => downloadFile(`/transactions/${t.id}/document`, `deal-record-${t.id}.pdf`)}
                              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                            >
                              Download Record
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <CancelDialog
        dialog={cancelDialog}
        onClose={() => setCancelDialog(null)}
        onConfirm={handleCancelConfirm}
        loading={cancelLoading}
        triggerRef={cancelTriggerRef}
      />
    </div>
  );
}
